import React, { useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { LockFill, Pencil, PlusLg, Search, Trash } from 'react-bootstrap-icons'
import Modal from '../../Modal/Modal'
import axiosService from '../../../utils/AxiosService'
import { toastError, toastSuccess } from '../../FormComponents/Toasts'
import { isUgcCreatorTag } from '../../../utils/tagStyles'
import TagNameModal from './TagNameModal'
import DeleteTagModal from './DeleteTagModal'

const byName = (a, b) => a.name.localeCompare(b.name)

export default observer(function TagsManagerModal({ isOpen, onClose, onTagsChanged = () => {} }) {
  const [tags, setTags] = useState([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [creating, setCreating] = useState(false)
  const [renaming, setRenaming] = useState(null)
  const [deleting, setDeleting] = useState(null)

  useEffect(() => {
    if (!isOpen) return
    setLoading(true)
    setQuery('')
    axiosService.get('/api/project_manager/tags/manage')
      .then(result => setTags(result))
      .catch(({ data: { error } }) => toastError(error))
      .finally(() => setLoading(false))
  }, [isOpen])

  const visibleTags = useMemo(() => {
    const term = query.trim().toLowerCase()
    return term ? tags.filter(tag => tag.name.toLowerCase().includes(term)) : tags
  }, [tags, query])

  const createTag = ({ name }, { setSubmitting }) => (
    axiosService.post('/api/project_manager/tags', { tag: { name: name.trim() } })
      .then(tag => {
        setTags(current => [...current, tag].sort(byName))
        setCreating(false)
        onTagsChanged()
        toastSuccess('Tag created')
      })
      .catch(({ data: { error } }) => {
        toastError(error)
        setSubmitting(false)
      })
  )

  const renameTag = ({ name }, { setSubmitting }) => (
    axiosService.patch(`/api/project_manager/tags/${renaming.id}`, { tag: { name: name.trim() } })
      .then(updated => {
        setTags(current => current.map(tag => (tag.id === updated.id ? { ...tag, name: updated.name } : tag)).sort(byName))
        setRenaming(null)
        onTagsChanged()
        toastSuccess('Tag renamed')
      })
      .catch(({ data: { error } }) => {
        toastError(error)
        setSubmitting(false)
      })
  )

  const deleteTag = () => (
    axiosService.delete(`/api/project_manager/tags/${deleting.id}`)
      .then(() => {
        setTags(current => current.filter(tag => tag.id !== deleting.id))
        setDeleting(null)
        onTagsChanged()
        toastSuccess('Tag deleted')
      })
      .catch(({ data: { error } }) => toastError(error))
  )

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} maxHeight="calc(100vh - 64px)">
        <Modal.Header>
          <span className="font-bold text-lg dark:text-white">Manage tags</span>
        </Modal.Header>
        <Modal.Body>
          <div className="w-[760px]">
            <div className="flex items-center justify-between gap-4 mb-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">Shared creator tags used across the team. Keep names consistent - PMs pick from this list when tagging creators.</p>
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="shrink-0 flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-rb-green rounded-lg dark:text-rb-dark-mode"
              >
                <PlusLg /> New tag
              </button>
            </div>

            <div className="rounded-xl border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter">
              <div className="px-4 py-3 border-b-1 border-rb-widget-border dark:border-rb-dark-mode-lighter flex items-center gap-2">
                <Search className="text-gray-400" />
                <input
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  placeholder={`Search ${tags.length} tags`}
                  className="flex-1 bg-transparent border-0 focus:ring-0 text-sm dark:text-white placeholder-gray-400"
                />
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold w-28 text-right">Creators</th>
                    <th className="px-4 py-3 font-semibold w-28 text-right">Accounts</th>
                    <th className="px-4 py-3 font-semibold w-32">Created</th>
                    <th className="px-4 py-3 w-24" />
                  </tr>
                </thead>
                <tbody>
                  {visibleTags.map(tag => {
                    const ugc = isUgcCreatorTag(tag)
                    return (
                      <tr key={tag.id} className="border-t-1 border-rb-widget-border dark:border-rb-dark-mode-lighter hover:bg-rb-grey/60 dark:hover:bg-rb-dark-mode-lighter/40">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${ugc ? 'bg-purple-600/20 text-purple-600 dark:text-purple-400' : 'bg-blue-600/15 text-blue-600 dark:text-blue-400'}`}>
                              {ugc ? `★ ${tag.name}` : tag.name}
                            </span>
                            {tag.system_key && (
                              <span title="System tag - used by the app and cannot be renamed or deleted" className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                <LockFill /> System
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right dark:text-white">{tag.creators_count}</td>
                        <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">{tag.accounts_count}</td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{tag.created_at ? new Date(tag.created_at).toLocaleDateString() : ''}</td>
                        <td className="px-4 py-3">
                          {!tag.system_key && (
                            <div className="flex justify-end gap-1">
                              <button type="button" title="Rename" onClick={() => setRenaming(tag)} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-rb-green dark:text-gray-300 dark:hover:bg-rb-dark-mode-lighter">
                                <Pencil />
                              </button>
                              <button type="button" title="Delete" onClick={() => setDeleting(tag)} className="p-2 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 dark:text-gray-300 dark:hover:bg-red-500/10">
                                <Trash />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                  {!loading && visibleTags.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center text-gray-500 dark:text-gray-400">{query ? 'No tags match your search.' : 'No tags yet.'}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {creating && <TagNameModal isOpen onClose={() => setCreating(false)} onSubmit={createTag} tags={tags} />}
      {renaming && <TagNameModal isOpen onClose={() => setRenaming(null)} onSubmit={renameTag} tags={tags} tag={renaming} />}
      <DeleteTagModal tag={deleting} onClose={() => setDeleting(null)} onConfirm={deleteTag} />
    </>
  )
})
